import { IAsyncActionCreator, createAsyncActionCreator } from '../store/actions/createActionCreator';
import { Delete } from '../utilities/Graft';
import { mapObject } from '../utilities/objects';
import { IFacet, pack, IFaceted } from '../models/Facet';
import { KeyFacet, stringPart, Key } from '../models/KeyFacet';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { updateItems } from '../store/reducers/itemMapReducer';
import { invalidationFacet, resolvedFacet, DemandResolution, IInvalidationFacetTrigger } from '../facets/DemandFacets';
import { readItems } from './readItems';
import { IItemCache } from '../models/IItemCacheObserver';
import { IActionWithEffect, processBatchWithEffects } from './processBatchWithEffects';

/**
 * @public
 */
export interface IInvalidateItemsPayload {
    items: {
        [itemKey: string]: IFacet<{}>[];
    };
}

const invalidationKeyFacet = new KeyFacet<{
    itemKey: string;
    facetId: string;
}>('invalidation', {
    itemKey: stringPart(),
    facetId: stringPart()
});

/**
 * Creates an action which, when dispatched, produces the batch actions and effect required to invalidate a set of items.
 * @internal
 */
export const invalidateItemsForBatch: IAsyncActionCreator<IInvalidateItemsPayload, IItemCacheThunkAction<IActionWithEffect>> =
    createAsyncActionCreator((payload: IInvalidateItemsPayload) => {
        const {
            items
        } = payload;

        return readItems((itemCache: IItemCache): IActionWithEffect => {
            // Build a queue of items and their facets to be invalidated.
            const pendingInvalidations: Key<typeof invalidationKeyFacet.type>[] = [];
            // Keep track of all items and facets seen to invalidate.
            const allInvalidations: {
                [invalidationKey: string]: boolean;
            } = {};

            const invalidateItemsPayloadItems: {
                [itemKey: string]: {
                    [facetId: string]: boolean;
                };
            } = {};

            const invalidateItemsPayloadCallbacks: {
                [itemKey: string]: {
                    [facetId: string]: boolean;
                };
            } = {};

            const queueInvalidation = (itemKey: string, facetId: string): void => {
                const invalidationKey = invalidationKeyFacet.serialize({
                    itemKey: itemKey,
                    facetId: facetId
                });

                if (!allInvalidations[invalidationKey]) {
                    allInvalidations[invalidationKey] = true;
                    pendingInvalidations.push(invalidationKey);

                    if (!invalidateItemsPayloadItems[itemKey]) {
                        invalidateItemsPayloadItems[itemKey] = {};
                    }

                    invalidateItemsPayloadItems[itemKey][facetId] = true;
                }
            };

            const invalidatedItems: (typeof updateItems.payloadType)['items'] = {};

            for (const itemKey in items) {
                if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
                    const facets = items[itemKey];

                    for (const facet of facets) {
                        queueInvalidation(itemKey, facet.id);
                    }
                }
            }

            const invalidationCallbacks: (() => IItemCacheThunkAction<void>)[] = [];
            let pendingInvalidation: Key<typeof invalidationKeyFacet.type> | undefined;

            while ((pendingInvalidation = pendingInvalidations.pop())) {
                const {
                    itemKey,
                    facetId
                } = invalidationKeyFacet.deserialize(pendingInvalidation);

                const invalidation = itemCache.demandItemFacet(invalidationFacet, itemKey);

                if (invalidation) {
                    const trigger = invalidation.triggers[facetId];

                    if (trigger) {
                        const {
                            targets = {},
                            callbacks = []
                        } = trigger;

                        for (const targetItemKey in targets) {
                            if (Object.prototype.hasOwnProperty.call(targets, targetItemKey)) {
                                const targetFacetIds = targets[targetItemKey];

                                for (const targetFacetId in targetFacetIds) {
                                    if (Object.prototype.hasOwnProperty.call(targetFacetIds, targetFacetId)) {
                                        if (targetFacetIds[targetFacetId]) {
                                            queueInvalidation(targetItemKey, targetFacetId);
                                        }
                                    }
                                }
                            }
                        }

                        if (callbacks.length > 0) {
                            if (!invalidateItemsPayloadCallbacks[itemKey]) {
                                invalidateItemsPayloadCallbacks[itemKey] = {};
                            }

                            invalidateItemsPayloadCallbacks[itemKey][facetId] = true;

                            invalidationCallbacks.push(...callbacks);
                        }
                    }
                }
            }

            for (const itemKey in invalidateItemsPayloadItems) {
                if (Object.prototype.hasOwnProperty.call(invalidateItemsPayloadItems, itemKey)) {
                    const facetIds = invalidateItemsPayloadItems[itemKey];

                    invalidatedItems[itemKey] = pack(
                        {
                            resolved: resolvedFacet,
                        },
                        {
                            resolved: {
                                demands: mapObject(facetIds, () => new Delete<DemandResolution>())
                            }
                        }
                    );
                }
            }

            for (const itemKey in invalidateItemsPayloadCallbacks) {
                if (Object.prototype.hasOwnProperty.call(invalidateItemsPayloadCallbacks, itemKey)) {
                    const facetIds = invalidateItemsPayloadCallbacks[itemKey];

                    invalidatedItems[itemKey] = {
                        ...(invalidatedItems[itemKey] || {}),
                        ...pack(
                            {
                                invalidation: invalidationFacet
                            },
                            {
                                invalidation: {
                                    triggers: mapObject(facetIds, () => new Delete())
                                }
                            }
                        )
                    };
                }
            }

            return {
                // These do not cause side-effects because they return un-dispatched actions.
                action: updateItems({
                    items: invalidatedItems
                }),
                effect: (dispatch: IItemCacheDispatch) => {
                    for (const invalidationCallback of invalidationCallbacks) {
                        dispatch(invalidationCallback());
                    }
                }
            };
        });
    });

/**
 * Creates an action which, when dispatched, applies the given item updates to the cache and marks
 * every mentioned facet as resolved.
 * @public
 */
export const invalidateItems: IAsyncActionCreator<IInvalidateItemsPayload, IItemCacheThunkAction<void>> =
    createAsyncActionCreator((payload: IInvalidateItemsPayload): IItemCacheThunkAction<void> => {
        return processBatchWithEffects({
            // The action will be dispatched by the batch processor.
            actionsWithEffects: [invalidateItemsForBatch(payload)]
        });
    });

/**
 * @public
 */
export interface IFacetTrigger<T> {
    /**
     * Sets up the target item's facets to be invalidated
     * when the packed item's trigger facets are invalidated.
     */
    thenInvalidate(itemKey: string, ...facets: IFacet<{}>[]): T;

    /**
     * Sets up the target item's invalidate callbacks
     * when the packed item's trigger facets are invalidated.
     */
    thenInvalidate(callback: () => IItemCacheThunkAction<void>): T;
}

/**
 * @public
 */
export interface IInvalidationFacetBuilder {
    /**
     * Sets up a trigger for a facet on the item being packed.
     */
    forFacet(facet: IFacet<{}>): IFacetTrigger<this>;
    /**
     * Packs the value for the invalidation facet.
     */
    pack(): IFaceted;
}

class InvalidationFacetBuilder implements IInvalidationFacetBuilder {
    private _targets: {
        facetId: string;
        itemKey: string;
        facets: IFacet<{}>[];
    }[];
    private _callbacks: {
        facetId: string;
        callback: () => IItemCacheThunkAction<void>;
    }[];

    constructor(targets: {
        facetId: string;
        itemKey: string;
        facets: IFacet<{}>[];
    }[], callbacks: {
        facetId: string;
        callback: () => IItemCacheThunkAction<void>;
    }[]) {
        this._targets = targets;
        this._callbacks = callbacks;
    }

    public forFacet(facet: IFacet<{}>): IFacetTrigger<this> {
        const facetId = facet.id;

        return {
            thenInvalidate: (itemKeyOrCallback: string | (() => IItemCacheThunkAction<void>), ...facets: IFacet<{}>[]) => {
                if (typeof itemKeyOrCallback === 'string') {
                    return new InvalidationFacetBuilder([...this._targets, {
                        facetId,
                        itemKey: itemKeyOrCallback,
                        facets
                    }], this._callbacks) as this;
                } else {
                    return new InvalidationFacetBuilder(this._targets, [...this._callbacks, {
                        facetId,
                        callback: itemKeyOrCallback
                    }]) as this;
                }
            }
        };
    }

    public pack(): IFaceted {
        const triggers: IFaceted<IInvalidationFacetTrigger> = pack({}, {});

        for (const {
            facetId,
            itemKey,
            facets
        } of this._targets) {
            // tslint:disable-next-line:no-non-null-assertion
            const targets = (triggers[facetId] || (triggers[facetId] = { targets: {} })).targets!;

            const invalidations = targets[itemKey] || (targets[itemKey] = pack({}, {}));

            for (const facet of facets) {
                invalidations[facet.id] = true;
            }
        }

        for (const {
            facetId,
            callback
        } of this._callbacks) {
            // tslint:disable-next-line:no-non-null-assertion
            const callbacks = (triggers[facetId] || (triggers[facetId] = { callbacks: [] })).callbacks!;

            callbacks.push(callback);
        }

        return invalidationFacet.pack({
            triggers
        });
    }
}

/**
 * Creates a helper for packing an invalidation facet.
 * @public
*/
export function getInvalidationFacet(): IInvalidationFacetBuilder {
    return new InvalidationFacetBuilder([], []);
}
